import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { motion } from 'framer-motion';
import { API, useAuth } from '@/App';
import Navbar from '@/components/Navbar';
import { Button } from '@/components/ui/button';
import { Eye, Lock, Crown, Globe } from 'lucide-react';
import { useIsIOSApp } from '@/hooks/useIsIOSApp';

const ProfileViewers = () => {
  const { user } = useAuth();
  const isIOSApp = useIsIOSApp();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (user) {
      fetchViewers();
    }
  }, [user]);

  const fetchViewers = async () => {
    try {
      const response = await axios.get(`${API}/profile-views/my-viewers`);
      setData(response.data);
    } catch (error) {
      console.error('Failed to fetch profile viewers');
    } finally {
      setLoading(false);
    }
  };

  if (!user) {
    return (
      <div className="min-h-screen bg-background">
        <Navbar />
        <div className="pt-24 text-center">
          <p>Please sign in to view this page</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Navbar />

      <div className="pt-24 pb-20 px-4 sm:px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            {/* Header */}
            <div className="text-center mb-12">
              <div className="inline-flex items-center justify-center w-20 h-20 bg-gradient-to-br from-primary via-primary to-accent rounded-full mb-6 shadow-lg">
                <Eye className="w-10 h-10 text-white" />
              </div>
              <h1 className="text-4xl md:text-5xl font-heading font-bold mb-4">
                Who Viewed Your Profile
              </h1>
              <p className="text-lg text-muted-foreground">
                See who's been checking out your profile
              </p>
            </div>

            {loading ? (
              <div className="text-center py-20">
                <div className="text-lg text-muted-foreground">Loading...</div>
              </div>
            ) : data?.premium_required ? (
              /* Premium Required */
              <div className="bg-white rounded-3xl border-2 border-border p-12 text-center">
                <div className="w-20 h-20 bg-accent/10 rounded-full flex items-center justify-center mx-auto mb-6">
                  <Lock className="w-10 h-10 text-accent" />
                </div>
                <h2 className="text-2xl font-heading font-bold mb-4">Premium Feature</h2>
                <p className="text-muted-foreground mb-8 max-w-md mx-auto">
                  {isIOSApp
                    ? 'Visit expatssphere.com in your browser to upgrade and see who viewed your profile.'
                    : 'Upgrade to membership to see who\'s viewed your profile in the last 30 days.'}
                </p>
                {!isIOSApp && (
                <Link to="/membership">
                  <Button className="bg-primary rounded-full h-12 px-8 font-bold">
                    <Crown className="w-5 h-5 mr-2" />
                    Become a Member
                  </Button>
                </Link>
                )}
              </div>
            ) : (
              <>
                {/* Stats */}
                <div className="bg-white rounded-3xl border-2 border-border p-8 mb-8">
                  <div className="text-center">
                    <div className="text-5xl font-heading font-bold text-primary mb-2">
                      {data?.total_views || 0}
                    </div>
                    <p className="text-muted-foreground">Profile views in the last 30 days</p>
                  </div>
                </div>

                {/* Viewers List */}
                {data?.viewers?.length > 0 ? (
                  <div className="bg-white rounded-3xl border-2 border-border overflow-hidden">
                    <div className="p-6 border-b border-border">
                      <h2 className="text-xl font-heading font-bold">Recent Viewers</h2>
                    </div>
                    <div className="divide-y divide-border">
                      {data.viewers.map((viewer, index) => (
                        <motion.div
                          key={viewer.viewer_id}
                          initial={{ opacity: 0, x: -20 }}
                          animate={{ opacity: 1, x: 0 }}
                          transition={{ duration: 0.3, delay: index * 0.05 }}
                        >
                          <Link to={`/profile/${viewer.viewer_id}`}>
                            <div className="p-4 hover:bg-muted/50 transition-colors flex items-center gap-4">
                              <div className="w-14 h-14 bg-gradient-to-br from-primary to-accent rounded-full flex items-center justify-center text-white font-bold flex-shrink-0 text-lg">
                                {viewer.profile_picture ? (
                                  <img src={viewer.profile_picture} alt="" className="w-full h-full rounded-full object-cover" />
                                ) : (
                                  viewer.viewer_name?.charAt(0) || '?'
                                )}
                              </div>
                              <div className="flex-1">
                                <p className="font-semibold text-lg">{viewer.viewer_name}</p>
                                <p className="text-muted-foreground text-sm flex items-center gap-1">
                                  <Globe className="w-4 h-4" />
                                  {viewer.viewer_nationality || 'Unknown nationality'}
                                </p>
                              </div>
                              <div className="text-right">
                                <p className="text-sm text-muted-foreground">
                                  {new Date(viewer.last_viewed).toLocaleDateString()}
                                </p>
                                {viewer.view_count > 1 && (
                                  <p className="text-xs text-accent font-medium">
                                    {viewer.view_count} visits
                                  </p>
                                )}
                              </div>
                            </div>
                          </Link>
                        </motion.div>
                      ))}
                    </div>
                  </div>
                ) : (
                  <div className="bg-white rounded-3xl border-2 border-border p-12 text-center">
                    <Eye className="w-16 h-16 mx-auto mb-4 text-muted-foreground/50" />
                    <h3 className="text-xl font-bold mb-2">No profile views yet</h3>
                    <p className="text-muted-foreground">
                      Complete your profile and join events to get noticed!
                    </p>
                  </div>
                )}
              </>
            )}
          </motion.div>
        </div>
      </div>
    </div>
  );
};

export default ProfileViewers;
